import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Flex, Button } from '@nula/core-ui';
import type { UIMessagePart, UIDataTypes, UITools } from 'ai';
import { MarkdownRenderer } from './markdown-renderer';
import { MessageToolbar, type FeedbackVote } from './message-toolbar';
import styles from './text-chunk-bubble.module.css';

const root = cva(styles.root);

/** Narrow the wide UIMessagePart union down to TextUIPart. */
type TextPart = Extract<UIMessagePart<UIDataTypes, UITools>, { type: 'text' }>;

interface TextChunkBubbleProps {
  /** A Vercel SDK v6 TextUIPart from useChat's messages[i].parts. */
  part: TextPart;
  /** Follow-up suggestion chips (M11b emits these via `data-text-suggestions`). */
  suggestions?: string[];
  onSuggestionClick?: (text: string) => void;
  /** When false, suggestion chips are hidden. Defaults to true. */
  isLatest?: boolean;
  /** Cortex run id for this message (from the data-run-id part). Used to
   *  associate user_score feedback with the LangSmith trace. */
  runId?: string;
  /** Called when the user picks a feedback vote. Parent opens the modal. */
  onVote?: (vote: FeedbackVote, runId: string | undefined) => void;
  className?: string;
}

/**
 * Renders the assistant's text bubble for a single TextUIPart.
 *
 * useChat accumulates `text-start` / `text-delta` / `text-end` chunks into
 * a single TextUIPart whose `text` field is the assembled string. The bubble
 * re-renders as deltas arrive; markdown is parsed every render. For long
 * messages this is fine — react-markdown is fast enough at the cadence the
 * SDK emits (one re-render per delta, typically <50 deltas per turn).
 *
 * Inline citations were dropped in M12b because Vercel SDK v6 TextUIPart has
 * no positional-citation field. Re-introducing them needs a custom data part
 * (`data-citations`) in a future milestone.
 */
export function TextChunkBubble({
  part,
  suggestions,
  onSuggestionClick,
  isLatest = true,
  runId,
  onVote,
  className,
}: TextChunkBubbleProps) {
  return (
    <Flex
      vertical
      gap="xs"
      className={clsx(root(), className)}
      data-testid="message-toolbar-wrapper"
    >
      <Flex vertical gap={0} className={styles.text}>
        <MarkdownRenderer>{part.text}</MarkdownRenderer>
      </Flex>

      {isLatest && suggestions && suggestions.length > 0 && (
        <Flex gap="xs" className={styles.suggestions}>
          {suggestions.map((suggestion) => (
            <Button
              key={suggestion}
              className={styles.suggestionChip}
              onClick={() => onSuggestionClick?.(suggestion)}
            >
              {suggestion}
            </Button>
          ))}
        </Flex>
      )}

      <MessageToolbar
        text={part.text}
        onVote={onVote ? (v) => onVote(v, runId) : undefined}
        className={styles.toolbar}
      />
    </Flex>
  );
}
